// Graphs — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as backtracking.js: concept → internals →
// the pattern's sub-variants → errors → complexity → beginner drills → cheatsheet → playground.

import graphDrills from './graphDrills';
const drill = id => graphDrills.find(d => d.id === id);

const graphs = {
  key: 'Graphs',
  tagline: 'Nodes and connections, with no rule against looping back — the structure behind maps, networks, and dependencies.',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to Graphs',
      type: 'text',
      body: [
        "A graph is a set of nodes (vertices) connected by edges. That's the entire definition — and it's deliberately looser than a tree's: a graph can have cycles (a path that loops back to where it started), a node can have any number of connections in any direction, and the whole structure doesn't need a single root or even be fully connected at all.",
        "Edges can be undirected (the connection works both ways — like a friendship) or directed (the connection only works one way — like a prerequisite: \"Calculus requires Algebra\" doesn't mean the reverse holds). This one distinction — directed or not — changes how cycles are detected and what a valid traversal even means, so it's the first thing worth identifying about any graph problem.",
      ],
    },
    {
      id: 'why',
      title: 'Why Graphs?',
      type: 'text',
      body: [
        "A tree is really just a special graph — one with no cycles and exactly one path between any two nodes. Real-world relationships are rarely that constrained: a road network has cycles (you can loop back to where you started), a social network has no single root, a dependency chain can accidentally contain a cycle (which is exactly the bug Course Schedule is designed to catch).",
        "Graphs are the right model whenever a problem is fundamentally about CONNECTIONS: is there a path from A to B? What's the shortest one? Can everything be reached from here? Do these connections contain a contradiction (a cycle where none should exist)? Once a problem is recognized as \"really a graph question,\" the toolbox is small and well-established: BFS, DFS, and Union-Find cover the overwhelming majority of what's asked.",
      ],
    },
    {
      id: 'analogy',
      title: 'How Graphs Work — A Metro Map and a One-Way Street Grid',
      type: 'text',
      heading: 'Two-Way Connections vs. One-Way Connections',
      body: [
        "Picture a city's metro map: stations are nodes, and the lines connecting them are edges. Crucially, if a line connects Station A to Station B, a train can travel in EITHER direction along it — that's an undirected graph. Asking \"can I get from A to B?\" and \"can I get from B to A?\" have the exact same answer, because the connection itself has no direction.",
        "Now picture a city's one-way street grid instead. A one-way street from A to B does NOT mean traffic can also flow from B to A — that's a directed graph. This distinction matters enormously: a cycle in a one-way grid (a loop you can drive around forever) is a completely different, and often more subtle, thing to detect than a cycle in a two-way road network — which is exactly why undirected and directed cycle detection use different techniques, covered later in this course.",
      ],
    },
    {
      id: 'visualize',
      title: 'Visualizing a Graph',
      type: 'graph-diagram',
      body: [
        "Unlike a tree, there's no single \"top\" node here, and following edges can lead back to where you started — node 0 connects to 1 and 2, both of which connect onward to 3, which also connects out to 4. Following 0 → 1 → 3 → 2 → 0 brings you right back to the start: that's a cycle.",
        "This is drawn as an undirected graph (no arrows) — every edge works in both directions.",
      ],
      demo: {
        nodes: [0, 1, 2, 3, 4],
        edges: [[0, 1], [0, 2], [1, 3], [2, 3], [3, 4]],
        directed: false,
        note: 'Path 0 → 1 → 3 → 2 → 0 returns to the start — a 4-node cycle, with node 4 hanging off as a non-cyclic tail.',
      },
    },
    {
      id: 'representation',
      title: 'Representing a Graph in Java',
      type: 'code',
      code:
`// Adjacency list — the standard choice for almost every graph problem
Map<Integer, List<Integer>> adj = new HashMap<>();
for (int[] edge : edges) {
    adj.computeIfAbsent(edge[0], k -> new ArrayList<>()).add(edge[1]);
    adj.computeIfAbsent(edge[1], k -> new ArrayList<>()).add(edge[0]);   // omit this line for a directed graph
}

// Adjacency matrix — simpler for dense graphs, or when O(1) edge lookups matter most
boolean[][] matrix = new boolean[n][n];
for (int[] edge : edges) {
    matrix[edge[0]][edge[1]] = true;
    matrix[edge[1]][edge[0]] = true;
}`,
      note: 'Adjacency lists win for almost every interview problem, because most graphs are SPARSE (far fewer edges than the V² a matrix always costs) — reach for a matrix only when the graph is genuinely dense or edge existence needs to be checked in true O(1).',
    },
    {
      id: 'bfs',
      title: 'Breadth-First Search (BFS)',
      type: 'code',
      code:
`void bfs(int start, Map<Integer, List<Integer>> adj) {
    Set<Integer> visited = new HashSet<>();
    Queue<Integer> queue = new LinkedList<>();
    visited.add(start);
    queue.add(start);

    while (!queue.isEmpty()) {
        int node = queue.poll();
        // process node here
        for (int neighbor : adj.get(node)) {
            if (visited.add(neighbor)) {
                queue.add(neighbor);
            }
        }
    }
}`,
      note: 'BFS explores level by level — everything 1 edge away, then everything 2 edges away, and so on. This is exactly why BFS (not DFS) is the right tool whenever a problem needs the SHORTEST path in an unweighted graph — it discovers closer nodes strictly before farther ones.',
    },
    {
      id: 'dfs',
      title: 'Depth-First Search (DFS)',
      type: 'code',
      code:
`void dfs(int node, Set<Integer> visited, Map<Integer, List<Integer>> adj) {
    visited.add(node);
    // process node here
    for (int neighbor : adj.get(node)) {
        if (!visited.contains(neighbor)) {
            dfs(neighbor, visited, adj);
        }
    }
}`,
      note: 'DFS dives as deep as possible before backtracking — the natural fit whenever a problem needs to explore every possible PATH (not just the shortest one), detect cycles, or determine full reachability/connectivity.',
    },
    {
      id: 'cycles',
      title: 'Detecting Cycles: Undirected vs. Directed',
      type: 'code',
      code:
`// Undirected: track the parent you came FROM — any visited neighbor that ISN'T
// the parent means you've reached an ancestor a different way. A cycle.
boolean hasCycleUndirected(int node, int parent, Set<Integer> visited, Map<Integer, List<Integer>> adj) {
    visited.add(node);
    for (int neighbor : adj.get(node)) {
        if (!visited.contains(neighbor)) {
            if (hasCycleUndirected(neighbor, node, visited, adj)) return true;
        } else if (neighbor != parent) {
            return true;
        }
    }
    return false;
}

// Directed: track a THIRD state — a node currently "on the active path" (VISITING).
// Reaching a VISITING node means a back edge to an ancestor. A cycle.
boolean hasCycleDirected(int node, int[] state, Map<Integer, List<Integer>> adj) {
    state[node] = 1; // VISITING
    for (int neighbor : adj.get(node)) {
        if (state[neighbor] == 1) return true;                          // back edge
        if (state[neighbor] == 0 && hasCycleDirected(neighbor, state, adj)) return true;
    }
    state[node] = 2; // VISITED
    return false;
}`,
      note: 'The parent-tracking trick only works for undirected graphs — a directed graph needs the extra VISITING state, because reaching an already-fully-finished (VISITED) node there is completely normal and NOT a cycle.',
    },
    {
      id: 'unionfind',
      title: 'Union-Find (Disjoint Set)',
      type: 'code',
      code:
`int[] parent;

int find(int x) {
    if (parent[x] != x) {
        parent[x] = find(parent[x]);   // path compression
    }
    return parent[x];
}

void union(int a, int b) {
    parent[find(a)] = find(b);
}

// Processing an edge (a, b): if find(a) == find(b) already, this edge closes a cycle.`,
      note: 'Union-Find answers "are these two nodes already connected?" in near O(1), without ever needing to traverse the graph — this is what makes it the standard tool for incrementally building a graph edge-by-edge while watching for the first one that creates a cycle.',
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Using undirected cycle detection on a directed graph (or vice versa)',
          why: 'Parent-tracking (the undirected technique) doesn\'t detect all cycles in a directed graph — a directed cycle can exist without ever revisiting a "parent" in the undirected sense. The two techniques are NOT interchangeable.',
          fix: 'Identify whether the graph is directed or undirected FIRST, and pick the matching cycle-detection technique — three-state DFS for directed, parent-tracking for undirected.',
        },
        {
          mistake: 'Forgetting to build the adjacency list for BOTH directions in an undirected graph',
          why: 'Adding edge[0]→edge[1] without also adding edge[1]→edge[0] silently turns an undirected graph into a directed one — traversal from one direction works, but from the other direction, a real connection is invisible.',
          fix: 'For undirected graphs, always add the edge to both nodes\' adjacency lists — this is the single most common graph-representation bug.',
        },
        {
          mistake: 'Forgetting to check for disconnected components',
          why: 'A single BFS/DFS call only explores whatever is reachable from ONE starting node — if the graph has multiple disconnected pieces, nodes in other pieces are never visited or counted at all.',
          fix: 'Loop over every node; for any node not yet visited, start a new BFS/DFS from it — this is what correctly counts connected components or catches nodes an initial traversal missed.',
        },
        {
          mistake: 'Using DFS when the shortest path (in an unweighted graph) is actually needed',
          why: 'DFS finds A path, but not necessarily the shortest one — it might dive down a long route before ever trying a shorter one.',
          fix: 'BFS is the correct tool for shortest paths in an unweighted graph — it discovers nodes in strict order of distance, guaranteeing the first time a target is reached is via the shortest path.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'BFS / DFS, full traversal', time: 'O(V + E)', note: 'Every node and every edge is visited at most a constant number of times.' },
        { op: 'Union-Find, with path compression', time: 'O(α(n)) amortized per operation', note: 'α is the inverse Ackermann function — effectively constant for any realistic input.' },
        { op: 'Union-Find, without path compression', time: 'O(n) per find, worst case', note: 'A long unflattened chain of parent pointers can be walked every single call.' },
        { op: 'Topological sort (DFS or Kahn\'s)', time: 'O(V + E)', note: 'Both approaches are a single traversal, just organized differently.' },
        { op: 'Adjacency matrix construction / storage', time: 'O(V²)', note: 'Regardless of how many edges actually exist.' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for graph techniques when you see…',
      body: [
        'Data with connections that AREN\'T strictly hierarchical — cycles are possible, or there\'s no single natural root.',
        'A grid (2D array) where movement between adjacent cells matters — this is a graph in disguise, with each cell as a node and adjacency as edges.',
        '"Is there a path from A to B?" or "what\'s the shortest path?" — BFS for unweighted shortest paths, DFS for reachability.',
        '"Does this set of dependencies/prerequisites contain a contradiction?" — cycle detection, directed-graph style.',
        'Incrementally connecting things while checking "would this new connection create a cycle?" — Union-Find, without needing a full traversal each time.',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('build-adjacency-list'),
            drill('graph-bfs-traversal'),
            drill('graph-dfs-traversal'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('number-of-islands-drill'),
            drill('detect-cycle-via-union-find'),
            drill('bipartite-graph-check'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('course-schedule-drill'),
            drill('topological-sort-kahns'),
            drill('redundant-connection-drill'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with graph representations, BFS/DFS, and Union-Find before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'Adjacency list is the default representation — O(V+E) space, versus a matrix\'s always-O(V²).',
        'BFS (queue) finds shortest paths in unweighted graphs — it discovers nodes in strict order of distance.',
        'DFS (recursion or an explicit stack) is the natural fit for exploring every path, detecting cycles, or checking full connectivity.',
        'Undirected cycle detection: track the parent you came from. Directed cycle detection: track a VISITING state — the two techniques are not interchangeable.',
        'Union-Find with path compression answers "already connected?" in near O(1) — the standard tool for building a graph edge-by-edge while watching for the first cycle-causing one.',
        'Always loop over every node to catch disconnected components — a single BFS/DFS only reaches what\'s connected to its own starting point.',
        'A 2D grid is a graph in disguise: each cell is a node, and up/down/left/right movement defines the edges.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default graphs;
